import React, { useCallback, useState } from 'react';

import { Table, type TablePaginationConfig } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'redux-first-router-link';
import { Text } from 'scouting-ui/lib';

import { MeritBadgeCounselorProfile } from '@appTypes/esb';
import { advancementTypes } from '@shared/constants';
import { goToYouthAdvRequirements } from '@shared/duck/actions';
import { getPreferredName } from '@shared/utils/preferredName';
import { sorters } from '@utils';

import { dashboardSortByNameSel, selectedYouthSel } from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import { compareByName } from '../duck/utils';

interface CounseledYouthTableProps {
  youths: MeritBadgeCounselorProfile['youths'];
}

export function MBCYouthsTable(props: CounseledYouthTableProps) {
  const { youths = [] } = props;
  const [pageSize, setPageSize] = useState(100);
  const selectedYouth = useSelector(selectedYouthSel);
  const sortByName = useSelector(dashboardSortByNameSel);
  const dispatch = useDispatch();
  type RowItem = (typeof youths)[number];

  const handleOnChange = useCallback((pagination: TablePaginationConfig) => {
    if (pagination.pageSize) {
      setPageSize(pagination.pageSize);
    }
  }, []);

  return (
    <Table
      key={sortByName}
      dataSource={youths}
      rowSelection={{
        selectedRowKeys: selectedYouth,
        onChange(selectedRowKeys: React.Key[]) {
          dispatch(
            mbcDashboardActions.setSelectedYouth(selectedRowKeys as number[]),
          );
        },
      }}
      rowKey={'memberId'}
      pagination={{ pageSize }}
      onChange={handleOnChange}
      columns={[
        {
          key: 'name',
          dataIndex: sortByName,
          defaultSortOrder: 'ascend',
          sorter: (rowA: RowItem, rowB: RowItem) =>
            compareByName(rowA, rowB, sortByName),
          title: <FormattedMessage id="mbcDashboard.youthTable.column.name" />,
          render: (_value: string, row) => {
            const fullName = getPreferredName(row);
            return <Text>{fullName}</Text>;
          },
        },
        {
          dataIndex: 'memberId',
          title: <FormattedMessage id="mbcDashboard.youthTable.column.mid" />,
          sorter: sorters.number('memberId'),
        },
        {
          dataIndex: 'meritBadgesCounseling',
          sorter: (a: RowItem, b: RowItem) =>
            a.meritBadgesCounseling.length - b.meritBadgesCounseling.length,
          title: <FormattedMessage id="mbcDashboard.youthTable.column.mbs" />,
          render(_value, record: (typeof youths)[number]) {
            return (
              <div>
                {record.meritBadgesCounseling.map((mb, index, mbs) => {
                  const isLast = index === mbs.length - 1;
                  const name = mb.meritBadgeName;
                  return (
                    <Link
                      to={goToYouthAdvRequirements({
                        advancementId: mb.meritBadgeId,
                        advancementType: advancementTypes.MERIT_BADGES,
                        userId: record.userId,
                      })}
                      onClick={() =>
                        dispatch(mbcDashboardActions.setNavigatedToMb(true))
                      }
                      key={mb.meritBadgeId}
                    >
                      <Text color="color-blue" underline key={mb.meritBadgeId}>
                        {name}
                      </Text>
                      {isLast ? '' : ', '}
                    </Link>
                  );
                })}
              </div>
            );
          },
        },
      ]}
    />
  );
}
